import { useState } from "react";
import { Icon } from "@iconify/react";
import { Tag, Skeleton } from "antd";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { cn } from "@/shared/lib/utils";
import { useCaregiverPatients } from "@/portals/caregiver/context/CaregiverPatientsContext";
import { acceptInviteApi, declineInviteApi } from "@/portals/caregiver/api/caregiverApi";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import type { CaregiverPatientAccessDetail, CaregiverPatientSummary, IncomingCaregiverInvite } from "@/shared/types/caregiverPortal";
import { RequestPatientModal } from "./RequestPatientModal";
import { RequestMoreAccessModal } from "./RequestMoreAccessModal";

function getInitials(name: string): string {
  return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p.charAt(0)).join("").toUpperCase();
}

// Abbreviated month — used for inline timestamps (banner "expires", card "Updated"/"Sent").
function formatDate(date: string | null): string {
  if (!date) return "";
  const parsed = new Date(date + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(parsed);
}

// Full month name — used for the Access Details panel's "Access granted"/"Expiration" rows.
function formatDateLong(date: string | null): string {
  if (!date) return "";
  const parsed = new Date(date + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(parsed);
}

function ViewingBanner({ name }: { name: string | null }) {
  return (
    <div className="flex min-h-[66px] w-fit min-w-[354px] items-center gap-3 rounded-2xl bg-[#180047] px-5 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10">
        <Icon icon="lucide:eye" className="h-4 w-4 text-[#F17893]" />
      </div>
      <div>
        <p className="text-sm font-extrabold !text-white">
          {name ? `Viewing ${name}'s shared records` : "Select a patient to view shared records"}
        </p>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-[#C7C2D6]">
          <Icon icon="lucide:lock" className="h-3 w-3" />
          Read-only access
        </p>
      </div>
    </div>
  );
}

interface PendingApprovalCardProps {
  patient: CaregiverPatientSummary;
  onViewTimeline: () => void;
}

function PendingApprovalCard({ patient, onViewTimeline }: PendingApprovalCardProps) {
  return (
    <div className="flex min-h-[168px] flex-col rounded-2xl border border-[#DDE1E8] bg-white p-5 shadow-[0_2px_5px_rgba(24,0,71,0.08)]">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#180047] text-xs font-bold !text-white">
          {getInitials(patient.name)}
        </div>
        <div>
          <h3 className="text-sm font-extrabold text-[#0A2947]">{patient.name}</h3>
          <p className="text-xs text-[#6B7280]">Patient</p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <Tag className="!m-0 !rounded-full !border-0 !bg-[#F3F4F6] !px-3 !text-[#6B7280]">{patient.categoriesTotalCount} categories</Tag>
        <Tag className="!m-0 !rounded-full !border-0 !bg-[#FEF3C7] !px-3 !text-[#B45309]">• Pending</Tag>
      </div>
      <Button
        variant="outline"
        onClick={onViewTimeline}
        className="!mx-auto !mt-4 !h-auto !w-fit !rounded-lg !border-[#F17893] !px-4 !py-1.5 !text-xs !font-semibold !text-[#F17893] hover:!border-[#F17893] hover:!bg-[#FFF2F4] hover:!text-[#F17893]"
      >
        View Timeline
      </Button>
    </div>
  );
}

interface IncomingInviteCardProps {
  invite: IncomingCaregiverInvite;
  isBusy: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

/**
 * A patient inviting THIS account as their caregiver (Direction B), where
 * the invitee already has a real account — requires an explicit decision
 * here, same as the notification bell's own Accept/Decline for this exact
 * event. This card is deliberately passive (no auto-resolve on a timer or
 * on mount): sitting here until acted on, however long that takes, IS the
 * correct "not yet decided" state — never auto-accept just because the
 * invite happened to be visible when this page loaded (see the bug this
 * replaced: an already-logged-in account got silently signed up as a
 * caregiver the instant an invite arrived, with no actual consent given).
 */
function IncomingInviteCard({ invite, isBusy, onAccept, onDecline }: IncomingInviteCardProps) {
  return (
    <div className="flex min-h-[168px] flex-col rounded-2xl border border-[#F17893] bg-[#FFF8F9] p-5 shadow-[0_2px_5px_rgba(24,0,71,0.08)]">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#180047] text-xs font-bold !text-white">
          {getInitials(invite.patientName)}
        </div>
        <div>
          <h3 className="text-sm font-extrabold text-[#0A2947]">{invite.patientName}</h3>
          <p className="text-xs text-[#6B7280]">Wants you as their caregiver</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {invite.categories.map((category) => (
          <Tag key={category} className="!m-0 !rounded-full !border-0 !bg-[#F3F4F6] !px-2.5 !text-[11px] !text-[#6B7280]">
            {category}
          </Tag>
        ))}
      </div>
      {invite.expiresDate && <p className="mt-2 text-xs text-[#9CA3AF]">Access until {formatDate(invite.expiresDate)}</p>}
      <div className="mt-auto flex items-center gap-2 pt-4">
        <Button
          onClick={onAccept}
          disabled={isBusy}
          className="!h-auto !flex-1 !rounded-lg !bg-[#F17893] !px-4 !py-1.5 !text-xs !font-semibold !text-white hover:!bg-[#E96C87]"
        >
          Accept
        </Button>
        <Button
          variant="outline"
          onClick={onDecline}
          disabled={isBusy}
          className="!h-auto !flex-1 !rounded-lg !border-[#E4002B]/50 !px-4 !py-1.5 !text-xs !font-semibold !text-[#E4002B] hover:!border-[#E4002B]/50 hover:!bg-[#FFF2F4] hover:!text-[#E4002B]"
        >
          Decline
        </Button>
      </div>
    </div>
  );
}

interface PatientCardProps {
  patient: CaregiverPatientSummary;
  isSelected: boolean;
  onSelect: () => void;
}

// Revoked patients are shown here for reference only — no action lives on
// this card. Once a patient revokes access, only that patient can grant it
// back (see the patient-side ManageCaregiversView "Give Access" action) —
// there is deliberately no caregiver-side way to ask for it again.
function PatientCard({ patient, isSelected, onSelect }: PatientCardProps) {
  const isRevoked = patient.status === "revoked";
  return (
    <Button
      variant="outline"
      onClick={onSelect}
      className={`!relative !min-h-[130px] !w-full !flex-col !items-start !justify-start !whitespace-normal !rounded-2xl !p-5 !text-left !shadow-[0_2px_5px_rgba(24,0,71,0.08)] ${
        isSelected ? "!border-2 !border-[#F17893] !bg-[#FFF2F4] hover:!border-[#F17893] hover:!bg-[#FFF2F4]" : isRevoked ? "!border-[#DDE1E8] !bg-[#FAFAFB] hover:!border-[#DDE1E8]" : "!border-[#DDE1E8] !bg-white hover:!border-[#F17893]/50 hover:!bg-white"
      }`}
    >
      {isSelected && (
        <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#E4002B]">
          <Icon icon="lucide:check" className="h-3 w-3 text-white" />
        </span>
      )}
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold !text-white ${isRevoked ? "bg-[#9CA3AF]" : "bg-[#180047]"}`}>
          {getInitials(patient.name)}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="break-words text-sm font-extrabold text-[#0A2947]">{patient.name}</h3>
          <p className="text-xs text-[#6B7280]">Patient</p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        {isRevoked ? (
          <Tag className="!m-0 !cursor-default !rounded-full !border-0 !bg-[#9CA3AF] !px-3 !text-white">Access Revoked</Tag>
        ) : (
          <>
            <Tag className={`!m-0 !rounded-full !border-0 !px-3 ${isSelected ? "!bg-[#FDE2E8] !text-[#E4002B]" : "!bg-[#F3F4F6] !text-[#6B7280]"}`}>{patient.categoriesSharedCount} categories</Tag>
            <span className="flex items-center gap-1 text-xs text-[#9CA3AF]">
              <Icon icon="lucide:lock" className="h-3 w-3" /> Read-only
            </span>
          </>
        )}
      </div>
    </Button>
  );
}

const CATEGORY_ICON_BG = "bg-[#F17893]";

function SharedRecordCard({
  category,
  onClick,
}: {
  category: CaregiverPatientAccessDetail["sharedCategories"][number];
  onClick: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      className="flex min-h-[132px] cursor-pointer flex-col overflow-hidden rounded-2xl border border-[#DDE1E8] bg-white shadow-[0_2px_5px_rgba(24,0,71,0.08)] transition hover:border-[#F17893]/60 hover:shadow-[0_4px_10px_rgba(24,0,71,0.12)]"
    >
      <div className="flex min-h-[68px] items-start gap-3 px-4 py-4">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${CATEGORY_ICON_BG}`}>
          <Icon icon={category.icon} className="h-4 w-4 text-white" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-extrabold text-[#0A2947]">{category.label}</h3>
          <p className="text-xs text-[#9CA3AF]">{category.subtitle}</p>
        </div>
      </div>
      <div className="mt-auto flex items-center justify-between border-t border-[#EEF0F4] px-4 py-3 text-[11px] text-[#A3AEC0]">
        <span className="flex items-center gap-1">
          <Icon icon="lucide:clock" className="h-3 w-3" />
          {category.updatedDate ? `Uploaded ${formatDate(category.updatedDate)}` : "Not uploaded yet"}
        </span>
        <span className="flex items-center gap-1">
          <Icon icon="lucide:lock" className="h-3 w-3" /> View only
        </span>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="min-h-full bg-[#FAFAFB] px-4 py-5 lg:px-6 lg:py-6">
      <Skeleton active paragraph={{ rows: 1 }} className="mb-5" />
      <div className="grid gap-4 sm:grid-cols-3">
        <Skeleton.Node active style={{ width: "100%", height: 120 }} />
        <Skeleton.Node active style={{ width: "100%", height: 120 }} />
        <Skeleton.Node active style={{ width: "100%", height: 120 }} />
      </div>
    </div>
  );
}

export function CaregiverDashboardView() {
  const navigate = useNavigate();
  const {
    patients,
    invites,
    isLoading,
    error,
    refresh,
    activePatientId,
    setActivePatientId,
    activePatientDetail: detail,
    isDetailLoading,
    refetchActivePatientDetail: refetchDetail,
  } = useCaregiverPatients();
  const [showRequestPatient, setShowRequestPatient] = useState(false);
  const [showRequestMoreAccess, setShowRequestMoreAccess] = useState(false);
  const [respondingToInviteId, setRespondingToInviteId] = useState<string | null>(null);

  const activePatient = patients.find((p) => p.id === activePatientId) ?? null;
  const pendingPatients = patients.filter((p) => p.status === "pending");
  // Revoked patients stay listed here too (not removed) — see PatientCard's
  // own "Access Revoked" treatment — so the caregiver can still find them
  // and the patient has someone to re-grant access to later, instead of the
  // relationship just disappearing.
  const activePatients = patients.filter((p) => p.status === "active" || p.status === "revoked");

  // Each incoming invite requires an explicit decision here — see
  // IncomingInviteCard's own docstring for why this can no longer auto-accept.
  const handleAcceptInvite = async (invite: IncomingCaregiverInvite) => {
    setRespondingToInviteId(invite.id);
    const { error: acceptError } = await acceptInviteApi(invite.id);
    setRespondingToInviteId(null);
    if (acceptError) {
      toastError(acceptError);
      return;
    }
    toastSuccess(`You're now ${invite.patientName}'s caregiver. You can view: ${invite.categories.join(", ")}`);
    refresh();
  };

  const handleDeclineInvite = async (invite: IncomingCaregiverInvite) => {
    setRespondingToInviteId(invite.id);
    const { error: declineError } = await declineInviteApi(invite.id);
    setRespondingToInviteId(null);
    if (declineError) {
      toastError(declineError);
      return;
    }
    refresh();
  };

  if (isLoading) return <DashboardSkeleton />;
  if (error) return <div className="min-h-full bg-[#F7F7F7] px-6 py-24 text-center text-sm font-semibold text-[#E4002B]">{error}</div>;

  const awaitingRequests = detail?.additionalRequests.filter((r) => r.status === "pending") ?? [];
  const resolvedRequests = detail?.additionalRequests.filter((r) => r.status !== "pending") ?? [];

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <ViewingBanner name={activePatient?.name ?? null} />
        <Button
          onClick={() => setShowRequestPatient(true)}
          className="!flex !h-auto !items-center !gap-2 !rounded-lg !bg-[#F17893] !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#E96C87]"
        >
          <Icon icon="lucide:plus" className="h-4 w-4 text-white" />
          Request Patient
        </Button>
      </div>

      {invites.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-3 text-base font-extrabold text-[#0A2947]">Caregiver Invitations</h2>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(245px,255px))] gap-5">
            {invites.map((invite) => (
              <IncomingInviteCard
                key={invite.id}
                invite={invite}
                isBusy={respondingToInviteId === invite.id}
                onAccept={() => handleAcceptInvite(invite)}
                onDecline={() => handleDeclineInvite(invite)}
              />
            ))}
          </div>
        </section>
      )}

      {pendingPatients.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-3 text-base font-extrabold text-[#0A2947]">Pending Approval</h2>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(245px,255px))] gap-5">
            {pendingPatients.map((patient) => (
              <PendingApprovalCard key={patient.id} patient={patient} onViewTimeline={() => navigate("/caregiver/access-activity")} />
            ))}
          </div>
        </section>
      )}

      <section className="mb-6">
        <h2 className="mb-3 text-base font-extrabold text-[#0A2947]">My Patients</h2>
        {activePatients.length === 0 ? (
          <EmptyState
            icon={<Icon icon="lucide:users" className="h-10 w-10 text-[#9CA3AF]" />}
            title="No patients yet"
            description="Once a patient accepts your request (or you accept theirs), they'll show up here."
          />
        ) : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(245px,255px))] gap-4">
            {activePatients.map((patient) => (
              <PatientCard
                key={patient.id}
                patient={patient}
                isSelected={patient.id === activePatientId}
                onSelect={() => setActivePatientId(patient.id)}
              />
            ))}
          </div>
        )}
      </section>

      {activePatient && (
        <>
          <section className="mb-6">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-base font-extrabold text-[#0A2947]">Shared Records</h2>
              <Tag className="!m-0 !rounded !border-[#E5E7EB] !bg-[#F3F4F6] !text-[#374151]">
                {detail?.categoriesSharedCount ?? 0} categories
              </Tag>
            </div>
            {isDetailLoading ? (
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
                <Skeleton.Node active style={{ width: "100%", height: 160 }} />
                <Skeleton.Node active style={{ width: "100%", height: 160 }} />
                <Skeleton.Node active style={{ width: "100%", height: 160 }} />
                <Skeleton.Node active style={{ width: "100%", height: 160 }} />
              </div>
            ) : detail && detail.sharedCategories.length > 0 ? (
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
                {detail.sharedCategories.map((category) => (
                  <SharedRecordCard
                    key={category.category}
                    category={category}
                    onClick={() => navigate(`/caregiver/patients/${activePatientId}/${category.category}`)}
                  />
                ))}
              </div>
            ) : detail?.accessStatus === "revoked" ? (
              <EmptyState
                icon={<Icon icon="lucide:folder-x" className="h-10 w-10 text-[#9CA3AF]" />}
                title="Access revoked"
                description={`${activePatient.name} has revoked your access. They can give it back at any time from their own dashboard.`}
              />
            ) : (
              <EmptyState
                icon={<Icon icon="lucide:folder-lock" className="h-10 w-10 text-[#9CA3AF]" />}
                title="Nothing shared yet"
                description={`${activePatient.name} hasn't shared any record categories with you.`}
              />
            )}
          </section>

          <section>
            <h2 className="mb-3 text-base font-extrabold text-[#0A2947]">Additional Access</h2>
            <div className="grid w-full items-start gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
            <div className="flex h-[420px] flex-col overflow-hidden rounded-2xl border border-[#DDE1E8] bg-white shadow-[0_2px_5px_rgba(24,0,71,0.08)]">
              <div className="shrink-0 p-5">
              <h3 className="text-sm font-extrabold text-[#0A2947]">Need to see more?</h3>
              <p className="mt-1 text-sm text-[#6B7280]">
                {detail?.accessStatus === "revoked"
                  ? `Your access was revoked. ${activePatient.name.split(" ")[0]} needs to give you access again before you can request more.`
                  : `${detail?.notSharedCategories.length ?? 0} categories not yet shared. You can request access, and ${activePatient.name.split(" ")[0]} will approve or decline.`}
              </p>
              <Button
                onClick={() => setShowRequestMoreAccess(true)}
                disabled={!detail || detail.accessStatus !== "active" || detail.notSharedCategories.length === 0}
                className="!mt-3 !flex !h-auto !items-center !gap-2 !rounded-lg !bg-[#F17893] !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#E96C87]"
              >
                <Icon icon="lucide:plus" className="h-4 w-4 text-white" />
                Request Access
              </Button>
              </div>

              {/* Fixed-height card, scrollable body — a caregiver with many outstanding/past
                  requests grows this list indefinitely otherwise, which stretches the card
                  (and the "Access Details" card beside it) well past the rest of the page. */}
              <div className="flex-1 overflow-y-auto">
              {awaitingRequests.length > 0 && (
                <div className="border-t border-[#EEF0F4] px-5 py-4">
                  <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-[#9CA3AF]">Awaiting Response</p>
                  <div className="space-y-2">
                    {awaitingRequests.map((request) => (
                      <div key={request.id} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-[#0A2947]">
                          {request.categoryLabel} <span className="text-xs text-[#9CA3AF]">Sent {formatDate(request.sentDate)}</span>
                        </span>
                        <Tag className="!m-0 !shrink-0 !rounded-full !border-0 !bg-[#FEF3C7] !px-3 !text-[#B45309]">• Pending</Tag>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resolvedRequests.length > 0 && (
                <div className="border-t border-[#EEF0F4] px-5 py-4">
                  <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-[#9CA3AF]">Previous Requests</p>
                  <div className="space-y-2">
                    {resolvedRequests.map((request) => (
                      <div key={request.id} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-[#0A2947]">
                          {request.categoryLabel} <span className="text-xs text-[#9CA3AF]">Sent {formatDate(request.sentDate)}</span>
                        </span>
                        <Tag
                          className={cn(
                            "!m-0 !shrink-0 !rounded-full !border-0 !px-3",
                            request.status === "approved" ? "!bg-[#D1FAE5] !text-[#047857]" : "!bg-[#FDE2E8] !text-[#E4002B]",
                          )}
                        >
                          • {request.status === "approved" ? "Approved" : "Declined"}
                        </Tag>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>

            <div className="min-h-[420px] rounded-2xl border border-[#DDE1E8] bg-white p-5 shadow-[0_2px_5px_rgba(24,0,71,0.08)]">
              <p className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0A2947]">
                <Icon icon="lucide:info" className="h-3.5 w-3.5 text-[#0A2947]" /> Access Details
              </p>
              <dl className="space-y-2.5 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-[#6B7280]">Patient</dt>
                  <dd className="font-semibold text-[#0A2947]">{detail?.patientName ?? activePatient.name}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-[#6B7280]">Access granted</dt>
                  <dd className="font-semibold text-[#0A2947]">{detail?.accessGrantedDate ? formatDateLong(detail.accessGrantedDate) : "—"}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-[#6B7280]">Expiration</dt>
                  <dd className={`font-semibold ${detail?.expiresDate ? "text-[#0A2947]" : "text-[#16A34A]"}`}>
                    {detail?.expiresDate ? formatDateLong(detail.expiresDate) : "No expiration set"}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-[#6B7280]">Access type</dt>
                  <dd className="flex items-center gap-1 font-semibold text-[#0A2947]">
                    <Icon icon="lucide:lock" className="h-3.5 w-3.5" /> Read-only
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-[#6B7280]">Categories</dt>
                  <dd className="font-semibold text-[#0A2947]">
                    {detail?.categoriesSharedCount ?? 0} of {detail?.categoriesTotalCount ?? 0} shared
                  </dd>
                </div>
              </dl>
              <div className="mt-4 rounded-xl bg-[#F9FAFB] p-3 text-xs leading-relaxed text-[#6B7280]">
                Only <span className="font-semibold text-[#0A2947]">{activePatient.name}</span> can extend, modify, or revoke your access. Contact them directly if you need changes.
              </div>
            </div>
            </div>
          </section>
        </>
      )}

      <RequestPatientModal open={showRequestPatient} onOpenChange={setShowRequestPatient} onRequested={refresh} />
      {activePatient && detail && (
        <RequestMoreAccessModal
          open={showRequestMoreAccess}
          onOpenChange={setShowRequestMoreAccess}
          patientId={activePatient.id}
          patientName={activePatient.name}
          notSharedCategories={detail.notSharedCategories}
          onRequested={refetchDetail}
        />
      )}
    </div>
  );
}
